import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, Typography, Button, Avatar, Paper, Snackbar, Alert, 
  ThemeProvider, createTheme, CssBaseline, Tabs, Tab,
  Card, CardContent, Grid, Chip, IconButton, LinearProgress,
  Fade, Zoom, Grow, Slide, Dialog, DialogTitle, DialogContent,
  DialogActions, List, ListItem, ListItemText, ListItemIcon,
  Badge, Tooltip
} from '@mui/material';
import {
  QrCodeScanner,
  Phone,
  Person,
  CameraAlt,
  CheckCircle,
  Error,
  AccessTime,
  Work,
  Coffee,
  ExitToApp,
  Refresh,
  Close,
  WifiOff,
  Wifi,
  CloudSync,
  CloudOff,
  Refresh as Retry,
  Warning,
  Info
} from '@mui/icons-material';
import { Html5QrcodeScanner } from 'html5-qrcode';
import axiosInstance from '../axiosConfig';
import { useLanguage } from '../../contexts/LanguageContext';
import networkManager from '../../utils/networkManager';
import offlineStorage from '../../utils/offlineStorage';

// Modern dark theme with gradients
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#667eea',
      light: '#8b9df6',
      dark: '#4c63d2',
    },
    secondary: {
      main: '#764ba2',
      light: '#9b6bc3',
      dark: '#5a3d7a',
    },
    background: {
      default: '#0a0a0a',
      paper: 'rgba(255, 255, 255, 0.05)',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b3b3b3',
    },
    success: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
    },
    error: {
      main: '#f44336',
      light: '#e57373',
      dark: '#d32f2f',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h3: {
      fontWeight: 700,
      fontSize: '2.5rem',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
    h4: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h6: {
      fontWeight: 500,
      fontSize: '1.25rem',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
          fontSize: '1rem',
        },
        contained: {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
            boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
            transform: 'translateY(-2px)',
          },
        },
        outlined: {
          border: '2px solid rgba(255, 255, 255, 0.2)',
          color: '#ffffff',
          '&:hover': {
            border: '2px solid rgba(255, 255, 255, 0.4)',
            background: 'rgba(255, 255, 255, 0.05)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
  },
});

const EmployeeAccess = () => {
  const { translate } = useLanguage();
  const [scannedQR, setScannedQR] = useState('');
  const [phoneDigits, setPhoneDigits] = useState('');
  const [accessMethod, setAccessMethod] = useState('qr');
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const [image, setImage] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [cameraActive, setCameraActive] = useState(false);
  const [availableActions, setAvailableActions] = useState([]);
  const [countdown, setCountdown] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [showPendingDialog, setShowPendingDialog] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [isRetrying, setIsRetrying] = useState(false);
  const qrReaderRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Network status management
  useEffect(() => {
    const removeListener = networkManager.addListener((event, online) => {
      setIsOnline(online);
      if (event === 'online') {
        showSnackbar('Conexión restaurada', 'success');
        updatePendingRequestsCount();
      } else if (event === 'offline') {
        showSnackbar('Sin conexión a internet', 'warning');
      }
    });

    updatePendingRequestsCount();

    return removeListener;
  }, []);

  const updatePendingRequestsCount = async () => {
    try {
      const count = await networkManager.getPendingRequestsCount();
      setPendingRequestsCount(count);
    } catch (error) {
      console.error('Error updating pending requests count:', error);
    }
  };

  const loadPendingRequests = async () => {
    try {
      const requests = await offlineStorage.getFailedRequests();
      setPendingRequests(requests);
    } catch (error) {
      console.error('Error loading pending requests:', error);
    }
  };

  const handleRetryPendingRequests = async () => {
    setIsRetrying(true);
    try {
      await networkManager.retryFailedRequests();
      showSnackbar('Reintentando solicitudes pendientes...', 'info');
      await updatePendingRequestsCount();
      await loadPendingRequests();
    } catch (error) {
      showSnackbar('Error al reintentar solicitudes', 'error');
    } finally {
      setIsRetrying(false);
    }
  };

  const handleClearPendingRequests = async () => {
    try {
      await networkManager.clearAllPendingRequests();
      setPendingRequestsCount(0);
      setPendingRequests([]);
      showSnackbar('Solicitudes pendientes eliminadas', 'info');
    } catch (error) {
      showSnackbar('Error al eliminar solicitudes pendientes', 'error');
    }
  };

  const startCamera = () => {
    setCameraActive(true);
    setError('');
  };

  const stopCamera = () => {
    setCameraActive(false);
  };

  useEffect(() => {
    if (!cameraActive || !qrReaderRef.current) return;

    const scanner = new Html5QrcodeScanner(
      'qr-reader',
      {
        fps: 10,
        qrbox: 250,
        facingMode: 'environment',
      },
      false
    );

    const onScanSuccess = async (decodedText) => {
      console.log("📸 Código QR escaneado:", decodedText);
      setScannedQR(decodedText);
      setIsProcessing(true);
      await validateQRCode(decodedText);
      scanner.clear();
      stopCamera();
      setIsProcessing(false);
    };

    const onScanError = (err) => {
      if (!err.message.includes("No MultiFormat Readers were able to detect the code")) {
        console.error('🚨 Error escaneando QR:', err);
        setError('Error al escanear el código QR');
      }
    };

    scanner.render(onScanSuccess, onScanError);

    return () => {
      scanner.clear();
    };
  }, [cameraActive, user]);

  const validateQRCode = async (qrCode) => {
    if (!qrCode) return;

    try {
      const response = await networkManager.makeRequest(
        {
          method: 'POST',
          url: `${process.env.REACT_APP_API_URL}/api/validate-qr`,
          data: { qr_code: qrCode }
        },
        {
          endpoint: 'validate-qr',
          maxRetries: 3,
          storeOnFailure: true
        }
      );

      // Verificar si la respuesta tiene contenido
      const responseText = await response.text();
      console.log('📡 Respuesta de validación QR:', responseText);
      console.log('📡 Longitud de respuesta:', responseText.length);
      
      if (!responseText || responseText.trim() === '') {
        throw new Error('Respuesta vacía del servidor - El servidor no devolvió datos');
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ Error parseando JSON:', parseError);
        console.error('❌ Contenido de respuesta:', responseText);
        throw new Error('Respuesta del servidor no es JSON válido');
      }

      if (data.success) {
        setUser(data.user);
        setError('');
        fetchAvailableActions(data.user.id);
        showSnackbar('Empleado validado exitosamente', 'success');
        await updatePendingRequestsCount();
      } else {
        setError('Código QR no válido');
        setUser(null);
        showSnackbar('Código QR inválido', 'error');
      }
    } catch (error) {
      console.error('❌ Error en validación:', error);
      
      if (!isOnline) {
        setError('Sin conexión a internet. La solicitud se reintentará automáticamente cuando se restaure la conexión.');
        showSnackbar('Sin conexión. Se reintentará automáticamente.', 'warning');
      } else {
        setError('Error al validar el código QR');
        showSnackbar('Error al validar el código QR', 'error');
      }
      
      await updatePendingRequestsCount();
    }
  };

  const validatePhoneDigits = async (digits) => {
    if (!digits || digits.length !== 4) return;

    setIsProcessing(true);
    try {
      const response = await networkManager.makeRequest(
        {
          method: 'POST',
          url: `${process.env.REACT_APP_API_URL}/api/validate-phone`,
          data: { phone_digits: digits }
        },
        {
          endpoint: 'validate-phone',
          maxRetries: 3,
          storeOnFailure: true
        }
      );

      // Verificar si la respuesta tiene contenido
      const responseText = await response.text();
      console.log('📡 Respuesta de validación teléfono:', responseText);
      console.log('📡 Longitud de respuesta:', responseText.length);
      
      if (!responseText || responseText.trim() === '') {
        throw new Error('Respuesta vacía del servidor - El servidor no devolvió datos');
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ Error parseando JSON:', parseError);
        console.error('❌ Contenido de respuesta:', responseText);
        throw new Error('Respuesta del servidor no es JSON válido');
      }

      if (data.success) {
        setUser(data.user);
        setError('');
        fetchAvailableActions(data.user.id);
        showSnackbar('Empleado validado exitosamente', 'success');
        await updatePendingRequestsCount();
      } else {
        setError('Dígitos del teléfono no válidos');
        setUser(null);
        showSnackbar('Dígitos del teléfono inválidos', 'error');
      }
    } catch (error) {
      console.error('❌ Error en validación:', error);
      
      if (!isOnline) {
        setError('Sin conexión a internet. La solicitud se reintentará automáticamente cuando se restaure la conexión.');
        showSnackbar('Sin conexión. Se reintentará automáticamente.', 'warning');
      } else {
        setError('Error al validar los dígitos del teléfono');
        showSnackbar('Error al validar los dígitos del teléfono', 'error');
      }
      
      await updatePendingRequestsCount();
    } finally {
      setIsProcessing(false);
    }
  };

  const fetchAvailableActions = async (userId) => {
    try {
      const response = await networkManager.makeRequest(
        {
          method: 'GET',
          url: `${process.env.REACT_APP_API_URL}/api/available-actions?user_id=${userId}`
        },
        {
          endpoint: 'available-actions',
          maxRetries: 2,
          storeOnFailure: false // Don't store this request as it's not critical
        }
      );

      // Verificar si la respuesta tiene contenido
      const responseText = await response.text();
      console.log('📡 Respuesta de acciones disponibles:', responseText);
      console.log('📡 Longitud de respuesta:', responseText.length);
      
      if (!responseText || responseText.trim() === '') {
        throw new Error('Respuesta vacía del servidor - El servidor no devolvió datos');
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ Error parseando JSON:', parseError);
        console.error('❌ Contenido de respuesta:', responseText);
        throw new Error('Respuesta del servidor no es JSON válido');
      }
      
      setAvailableActions(data.actions);
    } catch (error) {
      console.error('❌ Error obteniendo acciones disponibles:', error);
      // Set default actions if we can't fetch them
      setAvailableActions([
        { action: 'workstation_entry' },
        { action: 'break1_start' },
        { action: 'break1_end' },
        { action: 'workday_end' }
      ]);
    }
  };

  const captureImage = async () => {
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
    
      if (!video || !canvas) {
        console.error("Canvas or video element is not available.");
        return null;
      }
    
      // Request Camera Access
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      video.srcObject = stream;
    
      // Ensure video is loaded before playing
      await new Promise((resolve) => {
        video.onloadedmetadata = () => {
          video.play();
          resolve();
        };
      });
    
      // Wait 3 seconds for user preparation
      await new Promise((resolve) => setTimeout(resolve, 3000));
    
      // Capture Image
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
    
      if (!ctx) {
        console.error("Canvas context is null");
        return null;
      }
    
      ctx.drawImage(video, 0, 0);
    
      // Convert to blob
      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          stream.getTracks().forEach(track => track.stop());
          resolve(blob);
        }, 'image/png');
      });
    } catch (error) {
      console.error('Error capturing image:', error);
      return null;
    }
  };

  const startCountdown = async (action) => {
    setCountdown(3);
    
    for (let i = 3; i > 0; i--) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setCountdown(i - 1);
    }
    
    setCountdown(null);
    const imageBlob = await captureImage();
    await performAction(action, imageBlob);
  };

  const performAction = async (action, imageBlob) => {
    try {
      if (!imageBlob) {
        showSnackbar('Error: No se pudo capturar la imagen', 'error');
        return;
      }
  
      const formData = new FormData();
      formData.append('user_id', user.id);
      formData.append('user_type', user.user_type);
      formData.append('image', imageBlob, 'photo.png');
      
      let endpoint;
      
      if (action.startsWith('break')) {
        const parts = action.split('_');
        if (parts.length !== 2) {
          showSnackbar('Error: Acción de break incorrecta', 'error');
          return;
        }
        const [breakNumber, type] = parts;
        
        formData.append('break_number', breakNumber.replace('break', '')); 
        formData.append('type', type);
        endpoint = `${process.env.REACT_APP_API_URL}/api/register-break`;
      } else if (action === 'workstation_entry') {
        endpoint = `${process.env.REACT_APP_API_URL}/api/register-workstation-entry`;
      } else if (action === 'workday_end') {
        endpoint = `${process.env.REACT_APP_API_URL}/api/register-workday-end`;
      }

      // Store action locally for offline scenarios
      await offlineStorage.storePendingAction({
        action,
        userId: user.id,
        userType: user.user_type,
        timestamp: Date.now(),
        endpoint
      });
  
      const response = await networkManager.makeRequest(
        {
          method: 'POST',
          url: endpoint,
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        },
        {
          endpoint: `register-${action}`,
          maxRetries: 3,
          storeOnFailure: true
        }
      );
  
      showSnackbar('Acción registrada con éxito', 'success');
      setUser(null);
      setPhoneDigits('');
      await updatePendingRequestsCount();
    } catch (error) {
      console.error('❌ Error registrando acción:', error);
      
      if (!isOnline) {
        showSnackbar('Acción guardada localmente. Se enviará cuando se restaure la conexión.', 'warning');
      } else {
        showSnackbar('Error registrando acción. Se reintentará automáticamente.', 'error');
      }
      
      await updatePendingRequestsCount();
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'workstation_entry':
        return <Work />;
      case 'workday_end':
        return <ExitToApp />;
      case 'break1_start':
      case 'break2_start':
      case 'break3_start':
        return <Coffee />;
      case 'break1_end':
      case 'break2_end':
      case 'break3_end':
        return <Work />;
      default:
        return <AccessTime />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'workstation_entry':
        return 'success';
      case 'workday_end':
        return 'error';
      case 'break1_start':
      case 'break2_start':
      case 'break3_start':
        return 'warning';
      case 'break1_end':
      case 'break2_end':
      case 'break3_end':
        return 'info';
      default:
        return 'primary';
    }
  };

  const getActionText = (action) => {
    switch (action) {
      case 'break1_start':
        return translate('employeeAccess.startBreak1');
      case 'break1_end':
        return translate('employeeAccess.endBreak1');
      case 'break2_start':
        return translate('employeeAccess.startBreak2');
      case 'break2_end':
        return translate('employeeAccess.endBreak2');
      case 'break3_start':
        return translate('employeeAccess.startBreak3');
      case 'break3_end':
        return translate('employeeAccess.endBreak3');
      default:
        return action;
    }
  };

  const handlePhoneInput = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
    setPhoneDigits(value);
    
    if (value.length === 4) {
      validatePhoneDigits(value);
    }
  };

  const resetForm = () => {
    setUser(null);
    setError('');
    setPhoneDigits('');
    setScannedQR('');
    stopCamera();
  };
  
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box 
        sx={{ 
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background decorative elements */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 80%, rgba(102, 126, 234, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(118, 75, 162, 0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
          }}
        />

        <Box sx={{ maxWidth: 800, width: '100%', position: 'relative', zIndex: 1 }}>
          {/* Network Status and Pending Requests */}
          <Box sx={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 1, zIndex: 10 }}>
            <Tooltip title={isOnline ? 'Conectado' : 'Sin conexión'}>
              <Chip
                icon={isOnline ? <Wifi /> : <WifiOff />}
                label={isOnline ? 'Online' : 'Offline'}
                color={isOnline ? 'success' : 'error'}
                size="small"
                variant="outlined"
              />
            </Tooltip>
            
            {pendingRequestsCount > 0 && (
              <Tooltip title="Solicitudes pendientes">
                <Badge badgeContent={pendingRequestsCount} color="warning">
                  <IconButton
                    onClick={() => {
                      setShowPendingDialog(true);
                      loadPendingRequests();
                    }}
                    sx={{ 
                      background: 'rgba(255, 152, 0, 0.1)',
                      border: '1px solid rgba(255, 152, 0, 0.3)',
                      '&:hover': {
                        background: 'rgba(255, 152, 0, 0.2)',
                      }
                    }}
                  >
                    <CloudSync />
                  </IconButton>
                </Badge>
              </Tooltip>
            )}
          </Box>

          {/* Header */}
          <Fade in timeout={1000}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Core City Group
              </Typography>
              <Typography variant="h5" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                Sistema de Control de Acceso
              </Typography>
            </Box>
          </Fade>

          {!user ? (
            <Zoom in timeout={800}>
              <Card sx={{ p: 4, mb: 3 }}>
                <CardContent>
                  <Typography variant="h5" sx={{ mb: 3, textAlign: 'center', fontWeight: 600 }}>
                    {translate('employeeAccess.selectAccessMethod')}
                  </Typography>

                  <Tabs
                    value={accessMethod}
                    onChange={(e, newValue) => setAccessMethod(newValue)}
                    centered
                    sx={{ 
                      mb: 4,
                      '& .MuiTab-root': {
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        minHeight: 60,
                      },
                      '& .Mui-selected': {
                        color: '#667eea !important',
                      },
                      '& .MuiTabs-indicator': {
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        height: 3,
                        borderRadius: 2,
                      },
                    }}
                  >
                    <Tab 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <QrCodeScanner />
                          {translate('employeeAccess.qrCode')}
                        </Box>
                      } 
                      value="qr" 
                    />
                    <Tab 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone />
                          {translate('employeeAccess.phone')}
                        </Box>
                      } 
                      value="phone" 
                    />
                  </Tabs>

                  {accessMethod === 'qr' ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                        {translate('employeeAccess.scanQrCodeToAccess')}
                      </Typography>
                      
                      {!cameraActive ? (
                        <Button
                          variant="contained"
                          size="large"
                          startIcon={<QrCodeScanner />}
                          onClick={startCamera}
                          sx={{ 
                            py: 2, 
                            px: 4, 
                            fontSize: '1.1rem',
                            minWidth: 200
                          }}
                        >
                          {translate('employeeAccess.activateQrScanner')}
                        </Button>
                      ) : (
                        <Box sx={{ position: 'relative' }}>
                          <Paper 
                            elevation={8} 
                            sx={{ 
                              p: 3, 
                              background: 'rgba(0, 0, 0, 0.8)',
                              border: '2px solid rgba(102, 126, 234, 0.3)',
                              borderRadius: 3
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="h6" sx={{ color: 'primary.main' }}>
                                {translate('employeeAccess.qrScannerActive')}
                              </Typography>
                              <IconButton onClick={stopCamera} color="error">
                                <Close />
                              </IconButton>
                            </Box>
                            <div id="qr-reader" ref={qrReaderRef} style={{ width: '100%' }}></div>
                          </Paper>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                        {translate('employeeAccess.enterLast4Digits')}
                      </Typography>
                      
                      <Box sx={{ maxWidth: 300, mx: 'auto' }}>
                        <input
                          type="text"
                          value={phoneDigits}
                          onChange={handlePhoneInput}
                          placeholder="0000"
                          maxLength={4}
                          style={{
                            width: '100%',
                            padding: '16px 20px',
                            fontSize: '2rem',
                            textAlign: 'center',
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '2px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '12px',
                            color: '#ffffff',
                            outline: 'none',
                            fontFamily: 'monospace',
                            letterSpacing: '8px',
                            fontWeight: 'bold',
                          }}
                        />
                        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                          {translate('employeeAccess.onlyNumbers0to9')}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {isProcessing && (
                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                      <LinearProgress sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        Procesando...
                      </Typography>
                    </Box>
                  )}

                  {error && (
                    <Grow in timeout={300}>
                      <Alert 
                        severity="error" 
                        sx={{ mt: 3 }}
                        action={
                          <IconButton
                            color="inherit"
                            size="small"
                            onClick={() => setError('')}
                          >
                            <Close fontSize="inherit" />
                          </IconButton>
                        }
                      >
                        {error}
                      </Alert>
                    </Grow>
                  )}
                </CardContent>
              </Card>
            </Zoom>
          ) : (
            <Slide direction="up" in timeout={500}>
              <Card sx={{ p: 4 }}>
                <CardContent>
                  <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Avatar 
                      src={user.photo_url} 
                      sx={{ 
                        width: 120, 
                        height: 120, 
                        mx: 'auto', 
                        mb: 3,
                        border: '4px solid rgba(102, 126, 234, 0.3)',
                        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
                      }} 
                    />
                    <Typography variant="h4" sx={{ color: 'text.primary', mb: 1, fontWeight: 600 }}>
                      {user.name}
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'text.secondary', mb: 3 }}>
                      {user.position}
                    </Typography>
                    <Chip 
                      icon={<CheckCircle />} 
                      label={translate('employeeAccess.employeeVerified')} 
                      color="success" 
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>

                  <video ref={videoRef} autoPlay style={{ display: 'none' }}></video>
                  <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>

                  {countdown ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h3" sx={{ mb: 2, color: 'primary.main' }}>
                        {countdown}
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                        {translate('employeeAccess.prepareForCapture')}
                      </Typography>
                      <LinearProgress sx={{ mt: 3 }} />
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="h6" sx={{ mb: 3, textAlign: 'center', color: 'text.primary' }}>
                        {translate('employeeAccess.selectAction')}
                      </Typography>
                      
                      <Grid container spacing={2}>
                        {availableActions.map((action) => (
                          <Grid item xs={12} sm={6} md={4} key={action.action}>
                            <Button
                              fullWidth
                              variant="contained"
                              size="large"
                              startIcon={getActionIcon(action.action)}
                              onClick={() => startCountdown(action.action)}
                              sx={{ 
                                py: 3,
                                background: `linear-gradient(135deg, ${getActionColor(action.action) === 'success' ? '#4caf50' : 
                                  getActionColor(action.action) === 'error' ? '#f44336' :
                                  getActionColor(action.action) === 'warning' ? '#ff9800' :
                                  getActionColor(action.action) === 'info' ? '#2196f3' : '#667eea'} 0%, 
                                  ${getActionColor(action.action) === 'success' ? '#388e3c' : 
                                  getActionColor(action.action) === 'error' ? '#d32f2f' :
                                  getActionColor(action.action) === 'warning' ? '#f57c00' :
                                  getActionColor(action.action) === 'info' ? '#1976d2' : '#764ba2'} 100%)`,
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
                                },
                                transition: 'all 0.3s ease',
                              }}
                            >
                              {getActionText(action.action)}
                            </Button>
                          </Grid>
                        ))}
                      </Grid>

                      <Box sx={{ textAlign: 'center', mt: 4 }}>
                        <Button
                          variant="outlined"
                          startIcon={<Refresh />}
                          onClick={resetForm}
                          sx={{ px: 4 }}
                        >
                          {translate('employeeAccess.newAccess')}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Slide>
          )}
        </Box>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleSnackbarClose} 
            severity={snackbarSeverity} 
            sx={{ 
              width: '100%',
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>

        {/* Pending Requests Dialog */}
        <Dialog
          open={showPendingDialog}
          onClose={() => setShowPendingDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              background: 'rgba(0, 0, 0, 0.9)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 3
            }
          }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <CloudSync />
            Solicitudes Pendientes ({pendingRequestsCount})
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {pendingRequests.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CloudOff sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No hay solicitudes pendientes
                </Typography>
              </Box>
            ) : (
              <List>
                {pendingRequests.map((request, index) => (
                  <ListItem key={index} sx={{ 
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 2,
                    mb: 1,
                    background: 'rgba(255, 255, 255, 0.05)'
                  }}>
                    <ListItemIcon>
                      {request.retryCount >= 3 ? (
                        <Error color="error" />
                      ) : (
                        <Warning color="warning" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {request.endpoint}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Intentos: {request.retryCount}/3
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(request.timestamp).toLocaleString()}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button
              onClick={() => setShowPendingDialog(false)}
              variant="outlined"
            >
              Cerrar
            </Button>
            {pendingRequestsCount > 0 && (
              <>
                <Button
                  onClick={handleRetryPendingRequests}
                  variant="contained"
                  startIcon={<Retry />}
                  disabled={!isOnline || isRetrying}
                  sx={{
                    background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #45a049 0%, #2e7d32 100%)',
                    }
                  }}
                >
                  {isRetrying ? 'Reintentando...' : 'Reintentar'}
                </Button>
                <Button
                  onClick={handleClearPendingRequests}
                  variant="outlined"
                  color="error"
                  startIcon={<Close />}
                >
                  Limpiar Todo
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default EmployeeAccess;